import React from "react";
import {
  Autocomplete,
  Popper,
  TextField,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./autocompleteSelect.module.scss";

export type AutocompleteSelectOption = {
  id: number | string;
  name: string;
};

interface AutocompleteSelectProps {
  label: string;
  placeholder?: string;
  options: AutocompleteSelectOption[];
  value: AutocompleteSelectOption | null | AutocompleteSelectOption[];
  onChange: (
    value: AutocompleteSelectOption | null | AutocompleteSelectOption[],
  ) => void;
  onSearch?: (text: string) => void;
  disabled?: boolean;
  "data-testid"?: string;
  multiple?: boolean;
}

export const AutocompleteSelect = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  onSearch,
  disabled,
  "data-testid": dataTestId,
  multiple = false,
}: AutocompleteSelectProps) => {
  const PopperComponent = (popperProps: any) => {
    const width = popperProps?.anchorEl?.clientWidth;
    return (
      <Popper
        {...popperProps}
        className={styles.popper}
        style={{
          ...popperProps.style,
          zIndex: 20000,
          width,
        }}
      />
    );
  };

  // Ordenar opciones: seleccionadas primero, luego no seleccionadas
  const sortedOptions =
    multiple && Array.isArray(value)
      ? [
          ...options.filter((opt) => value.some((v) => v.id === opt.id)),
          ...options.filter((opt) => !value.some((v) => v.id === opt.id)),
        ]
      : options;

  // Calcular texto a mostrar en el campo
  const getDisplayText = () => {
    if (!multiple || !Array.isArray(value)) {
      return placeholder;
    }
    if (value.length === 0) {
      return placeholder;
    }
    if (value.length === 1) {
      return value[0]?.name ?? placeholder;
    }
    return `(${value.length}) deducciones seleccionadas`;
  };

  // Función para renderizar cada opción con estilos personalizados
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AutocompleteSelectOption,
    state: any,
  ): React.ReactNode => {
    const isSelected =
      multiple && Array.isArray(value) && value.some((v) => v.id === option.id);

    const handleRemove = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (multiple && Array.isArray(value)) {
        const newValue = value.filter((v) => v.id !== option.id);
        onChange(newValue);
      }
    };

    return (
      <li {...props}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontWeight: isSelected ? "bold" : "normal",
              fontSize: "0.875rem",
            }}
          >
            {option.name}
          </Typography>
          {isSelected && (
            <IconButton
              size="small"
              onClick={handleRemove}
              data-testid={`remove-option-${option.id}`}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </li>
    );
  };

  return (
    <div className={styles.container} data-testid={dataTestId}>
      <span className={styles.label}>{label}</span>
      <Autocomplete
        className={styles.autocomplete}
        disablePortal={false}
        openOnFocus
        multiple={multiple}
        options={sortedOptions}
        value={value}
        disabled={!!disabled}
        PopperComponent={PopperComponent}
        isOptionEqualToValue={(option, val) => {
          if (multiple && Array.isArray(val)) {
            return val.some((v) => v.id === option.id);
          }
          return option.id === val?.id;
        }}
        getOptionLabel={(option) => option?.name ?? ""}
        onChange={(_, newValue) => {
          onChange(newValue);
        }}
        onInputChange={(_, newInputValue, reason) => {
          if (!onSearch) return;
          if (reason === "input") onSearch(newInputValue);
        }}
        renderTags={() => null}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={getDisplayText()}
            variant="outlined"
            className={styles.textField}
          />
        )}
      />
    </div>
  );
};
